import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useThemeStore, type ThemeMode } from './theme'
import { setLocale, type AppLocale } from '@/locales'

export type AccentColor = 'blue' | 'green' | 'purple' | 'orange' | 'pink'
export type Density = 'comfortable' | 'compact'

export interface AppearanceState {
  theme: ThemeMode
  accent: AccentColor
  density: Density
  lang: AppLocale | 'auto'
}

const STORAGE_KEY = 'afd.appearance'

// Hue/saturation/lightness triples for each accent (in HSL components — match CSS var convention).
// We mutate --brand-500/600 etc. so Tailwind classes that reference `hsl(var(--brand-500))`
// automatically pick up the new accent color.
const ACCENTS: Record<AccentColor, Record<string, string>> = {
  blue: {
    '50': '214 100% 97%',
    '100': '214 95% 93%',
    '200': '213 97% 87%',
    '300': '212 96% 78%',
    '400': '213 94% 68%',
    '500': '217 91% 60%',
    '600': '221 83% 53%',
    '700': '224 76% 48%',
    '800': '226 71% 40%',
    '900': '224 64% 33%',
  },
  green: {
    '50': '151 81% 96%',
    '100': '151 81% 90%',
    '200': '149 80% 81%',
    '300': '152 76% 70%',
    '400': '154 72% 58%',
    '500': '158 64% 45%',
    '600': '161 62% 38%',
    '700': '163 65% 32%',
    '800': '164 71% 26%',
    '900': '166 73% 21%',
  },
  purple: {
    '50': '270 100% 98%',
    '100': '269 100% 95%',
    '200': '269 100% 91%',
    '300': '269 97% 85%',
    '400': '270 95% 76%',
    '500': '271 81% 60%',
    '600': '271 70% 53%',
    '700': '272 60% 46%',
    '800': '272 51% 38%',
    '900': '272 49% 30%',
  },
  orange: {
    '50': '33 100% 96%',
    '100': '34 100% 91%',
    '200': '32 100% 83%',
    '300': '31 100% 72%',
    '400': '27 96% 61%',
    '500': '24 95% 53%',
    '600': '21 90% 48%',
    '700': '17 86% 42%',
    '800': '15 79% 36%',
    '900': '15 75% 30%',
  },
  pink: {
    '50': '327 73% 97%',
    '100': '326 85% 93%',
    '200': '326 87% 86%',
    '300': '327 86% 77%',
    '400': '329 81% 67%',
    '500': '330 81% 60%',
    '600': '333 71% 53%',
    '700': '335 65% 47%',
    '800': '336 62% 40%',
    '900': '336 64% 33%',
  },
}

function readPersisted(): AppearanceState {
  const fallback: AppearanceState = {
    theme: 'auto',
    accent: 'blue',
    density: 'comfortable',
    lang: 'auto',
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return fallback
    const parsed = JSON.parse(raw) as Partial<AppearanceState>
    return {
      theme: parsed.theme === 'light' || parsed.theme === 'dark' || parsed.theme === 'auto' ? parsed.theme : 'auto',
      accent: ACCENTS[parsed.accent as AccentColor] ? (parsed.accent as AccentColor) : 'blue',
      density: parsed.density === 'compact' ? 'compact' : 'comfortable',
      lang: parsed.lang === 'zh-CN' || parsed.lang === 'en' || parsed.lang === 'auto' ? parsed.lang : 'auto',
    }
  } catch {
    return fallback
  }
}

function writePersisted(state: AppearanceState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    /* ignore */
  }
}

function applyAccent(accent: AccentColor): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  const colors = ACCENTS[accent]
  for (const [shade, value] of Object.entries(colors)) {
    root.style.setProperty(`--brand-${shade}`, value)
  }
  root.dataset.accent = accent
}

function applyDensity(density: Density): void {
  if (typeof document === 'undefined') return
  document.documentElement.dataset.density = density
  document.documentElement.classList.toggle('density-compact', density === 'compact')
}

function detectSystemLocale(): AppLocale {
  if (typeof navigator === 'undefined') return 'zh-CN'
  const lang = navigator.language.toLowerCase()
  if (lang.startsWith('zh')) return 'zh-CN'
  if (lang.startsWith('en')) return 'en'
  return 'zh-CN'
}

export const useAppearanceStore = defineStore('appearance', () => {
  const initial = readPersisted()
  const themeMode = ref<ThemeMode>(initial.theme)
  const accent = ref<AccentColor>(initial.accent)
  const density = ref<Density>(initial.density)
  const langPref = ref<AppLocale | 'auto'>(initial.lang)

  const effectiveLocale = computed<AppLocale>(() =>
    langPref.value === 'auto' ? detectSystemLocale() : langPref.value,
  )

  function setTheme(mode: ThemeMode): void {
    themeMode.value = mode
    const theme = useThemeStore()
    theme.setMode(mode)
    persist()
  }

  function setAccent(next: AccentColor): void {
    accent.value = next
    applyAccent(next)
    persist()
  }

  function setDensity(next: Density): void {
    density.value = next
    applyDensity(next)
    persist()
  }

  function setLangPref(next: AppLocale | 'auto'): void {
    langPref.value = next
    setLocale(effectiveLocale.value)
    persist()
  }

  function persist(): void {
    writePersisted({
      theme: themeMode.value,
      accent: accent.value,
      density: density.value,
      lang: langPref.value,
    })
  }

  function applyAll(): void {
    applyAccent(accent.value)
    applyDensity(density.value)
    setLocale(effectiveLocale.value)
    // Theme apply is handled by theme store at startup.
  }

  return {
    themeMode,
    accent,
    density,
    langPref,
    effectiveLocale,
    setTheme,
    setAccent,
    setDensity,
    setLangPref,
    applyAll,
  }
})
